import { API_BASE_URL } from '../config/env';
import { ApiError } from './apiError';
import type { ApiResponse } from './apiResponse';

let csrf: string | null = null;
let contextVersion: string | null = null;
let onContextChanged = () => {};
let onExpired = () => {};
let generation = 0;
const pending = new Set<AbortController>();
let bootstrapFlight: Promise<unknown> | null = null;
export function configureTransport(next: { onExpired: () => void; onContextChanged?: () => void }) { onExpired = next.onExpired; onContextChanged = next.onContextChanged || (() => {}); }
// Also rejects late responses even when an adapter ignores AbortSignal.
export function invalidateScope() { generation++; for (const controller of pending) controller.abort(); pending.clear(); }
export function bootstrapSession<T>(): Promise<T> {
  if (!bootstrapFlight) bootstrapFlight = request<T>('/auth/session', { auth: false }).finally(() => { bootstrapFlight = null; });
  return bootstrapFlight as Promise<T>;
}
interface RequestOptions extends Omit<RequestInit, 'body'> { body?: unknown; auth?: boolean; retried?: boolean }
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  if (!/^\/[a-zA-Z0-9/_-]*(\?.*)?$/.test(path) || path.includes('..')) throw new Error('Đường dẫn API không hợp lệ.');
  const { body, auth = true, retried = false, ...init } = options;
  const method = (init.method || 'GET').toUpperCase();
  if (method !== 'GET' && !path.startsWith('/auth/')) throw new ApiError('Web hiện chỉ hỗ trợ xem dữ liệu.', 403, 1048);
  if (method !== 'GET' && !csrf) await bootstrapSession();
  const currentGeneration = generation;
  const controller = new AbortController(); pending.add(controller);
  const timeout = setTimeout(() => controller.abort(), 30_000);
  const abort = () => controller.abort();
  if (init.signal?.aborted) controller.abort();
  init.signal?.addEventListener('abort', abort, { once: true });
  const headers = new Headers(init.headers);
  headers.delete('Authorization'); headers.delete('Cookie');
  headers.set('Accept', 'application/json');
  // The BFF's CSRF guard rejects any write that is not JSON (403 / 1047), so every non-GET carries a JSON
  // body even when it has nothing to say, e.g. logout.
  const payload = body !== undefined ? JSON.stringify(body) : method === 'GET' ? undefined : '{}';
  if (payload !== undefined) headers.set('Content-Type', 'application/json');
  if (method !== 'GET' && csrf) headers.set('X-CSRF-Token', csrf);
  if (contextVersion) headers.set('X-StoreX-Context', contextVersion);
  let response: Response; let rawBody: string;
  try {
    response = await fetch(API_BASE_URL + path, { ...init, method, headers,
      body: payload, signal: controller.signal,
      credentials: 'same-origin', redirect: 'error', cache: 'no-store' });
    rawBody = await response.text();
  } catch (error) {
    if (init.signal?.aborted || currentGeneration !== generation) throw new DOMException('Workspace changed', 'AbortError');
    throw new ApiError(controller.signal.aborted ? 'Kết nối quá thời gian chờ. Vui lòng thử lại.' : 'Không kết nối được BFF DEV. Vui lòng thử lại.', 0, 0);
  } finally { pending.delete(controller); clearTimeout(timeout); init.signal?.removeEventListener('abort', abort); }
  if (currentGeneration !== generation) throw new DOMException('Workspace changed', 'AbortError');
  // A temporary upstream/transport failure preserves the session; only confirmed 401 expires it.
  if (response.status === 401) {
    csrf = null; contextVersion = null;
    if (auth) onExpired();
    // Anonymous login page left open past its session: BFF rejected before any upstream call, so one retry is safe.
    else if (method !== 'GET' && !retried) { await bootstrapSession(); return request<T>(path, { ...options, retried: true }); }
  }
  let envelope: ApiResponse<T>;
  try { envelope = JSON.parse(rawBody) as ApiResponse<T>; }
  catch { throw new ApiError('BFF DEV trả về dữ liệu không hợp lệ.', response.status, 0); }
  if (!envelope || typeof envelope !== 'object' || typeof envelope.code !== 'number' || typeof envelope.message !== 'string') throw new ApiError('Response API không đúng định dạng.', response.status, 0);
  if (response.status === 409 && envelope.code === 1049 && auth) onContextChanged();
  if (!response.ok || envelope.code !== 0) throw new ApiError(envelope.message || `Không thể thực hiện yêu cầu (${response.status}).`, response.status, envelope.code, envelope.data);
  if (envelope.status !== 'success' || !('data' in envelope)) throw new ApiError('Response API không đúng định dạng.', response.status, 0);
  const data = envelope.data as { csrf_token?: string; context_version?: string; authenticated?: boolean; logged_out?: boolean };
  if (typeof data?.csrf_token === 'string') csrf = data.csrf_token;
  if (typeof data?.context_version === 'string') contextVersion = data.context_version;
  if (data?.authenticated === false) contextVersion = null;
  if (data?.logged_out) { csrf = null; contextVersion = null; }
  return envelope.data;
}
export function queryString(values: Record<string, string | number | undefined | null>) {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => { if (value !== undefined && value !== null && value !== '') params.set(key, String(value)); });
  return params.size ? `?${params}` : '';
}
